// Water balance helper: ingresos (oral) vs egresos totales (orina + vomito + drenajes + insensible).
//
// Insensible loss formula for adults: weight_kg × 0.5 ml/kg/hour × shift_hours.
// Standard clinical reference (adult, no fever): ~10-15 ml/kg/day = 0.4-0.6 ml/kg/hr.
// We use 0.5 ml/kg/hr (the common nursing-practice approximation).
//
// Tolerance for "balanced" classification: ±500 ml per shift.
// (was ±300 in v1; bumped because total outputs now include insensible and drains)

import { computeShiftDuration } from "@/lib/duration";

const TOLERANCE_ML = 500;
const INSENSIBLE_PER_KG_HR = 0.5;

export function parseMl(value) {
  if (value === undefined || value === null || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function computeInsensibleLoss(weightKg, shift) {
  const w = parseMl(weightKg);
  if (w <= 0 || !shift) return 0;
  const dur = computeShiftDuration(shift);
  if (!dur) return 0;
  return Math.round(w * INSENSIBLE_PER_KG_HR * (dur.totalMinutes / 60));
}

export function computeWaterBalance(closing, shift) {
  if (!closing) return null;
  const intake = parseMl(closing.nutrition?.water_oral_ml);
  const urine = parseMl(closing.elimination?.urine_amount_ml);
  const vomit = parseMl(closing.elimination?.vomit_ml);
  const drain = parseMl(closing.elimination?.drain_ml);
  const insensible = computeInsensibleLoss(closing.vitals?.weight_kg, shift);

  const output = urine + vomit + drain + insensible;
  if (intake === 0 && output === 0) return null;

  const balance = intake - output;
  let status;
  if (Math.abs(balance) <= TOLERANCE_ML) status = "balanced";
  else if (balance > 0) status = "positive";
  else status = "negative";

  return {
    intake,
    output,
    breakdown: { urine, vomit, drain, insensible },
    balance,
    status,
  };
}

export const BALANCE_STATUS = {
  balanced: { label: "Equilibrado", color: "#15803D", bg: "#D1FAE5", dot: "#10B981" },
  positive: { label: "Positivo",   color: "#1D4ED8", bg: "#DBEAFE", dot: "#3B82F6" },
  negative: { label: "Negativo",   color: "#B91C1C", bg: "#FEE2E2", dot: "#DC2626" },
};
